if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        const sectionId = this.dataset.sectionId;
        const find = (id) =>
          this.querySelector(`[id="${id}-${sectionId}"]`);

        this.recipientFieldsLiveRegion = find(
          'Recipient-fields-live-region'
        );
        this.checkboxInput = find('Recipient-checkbox');
        this.hiddenControlField = find('Recipient-control');
        this.emailInput = find('Recipient-email');
        this.nameInput = find('Recipient-name');
        this.messageInput = find('Recipient-message');
        this.sendonInput = find('Recipient-send-on');
        this.offsetProperty = find('Recipient-timezone-offset');

        this.errorMessageWrapper = this.querySelector(
          '.product-form__recipient-error-message-wrapper'
        );
        this.errorMessageList =
          this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage =
          this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader =
          this.errorMessage?.textContent.trim() ||
          'Please check the recipient details.';

        this.productForm = this.closest('form');

        if (
          !this.checkboxInput ||
          !this.hiddenControlField ||
          !this.emailInput ||
          !this.productForm
        ) {
          console.error(
            'Recipient form: required fields or enclosing product form are missing.',
            this
          );
          return;
        }

        this.initialized = true;

        this.checkboxInput.disabled = false;
        this.hiddenControlField.disabled = true;

        if (this.offsetProperty) {
          this.offsetProperty.value = String(
            new Date().getTimezoneOffset()
          );
        }

        this.handleChange = () => this.onChange();

        this.handleCartAdded = () => {
          this.resetRecipientForm();
        };

        this.handleCartError = (event) => {
          const error = event.detail?.errorMessage;
          this.displayErrorMessage(
            '',
            error || 'Unable to add gift card. Please try again.'
          );
        };

        this.addEventListener('change', this.handleChange);

        this.productForm.addEventListener(
          'ajaxProduct:added',
          this.handleCartAdded
        );

        this.productForm.addEventListener(
          'ajaxProduct:error',
          this.handleCartError
        );

        this.onChange();
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.removeEventListener('change', this.handleChange);

        this.productForm?.removeEventListener(
          'ajaxProduct:added',
          this.handleCartAdded
        );

        this.productForm?.removeEventListener(
          'ajaxProduct:error',
          this.handleCartError
        );

        this.initialized = false;
      }

      onChange() {
        const enabled = this.checkboxInput.checked;

        // The hidden fallback is only for when JavaScript is unavailable.
        this.hiddenControlField.disabled = true;

        if (enabled) {
          this.enableInputFields();
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
        }

        if (this.recipientFieldsLiveRegion) {
          this.recipientFieldsLiveRegion.textContent = enabled
            ? window.accessibilityStrings?.recipientFormExpanded ||
              'Gift card recipient fields expanded'
            : window.accessibilityStrings?.recipientFormCollapsed ||
              'Gift card recipient fields collapsed';
        }
      }

      inputFields() {
        return [
          this.emailInput,
          this.nameInput,
          this.messageInput,
          this.sendonInput
        ].filter(Boolean);
      }

      disableableFields() {
        return [
          ...this.inputFields(),
          this.offsetProperty
        ].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = false;
        });

        this.emailInput.required = true;
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = true;
        });

        this.emailInput.required = false;
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();

        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.hidden = false;

        const isFieldErrors =
          body &&
          typeof body === 'object' &&
          !Array.isArray(body);

        if (!isFieldErrors) {
          if (this.errorMessage) {
            this.errorMessage.textContent = Array.isArray(body)
              ? body.join(', ')
              : String(body || title || 'Unable to add gift card.');
          }
          return;
        }

        if (this.errorMessage) {
          this.errorMessage.textContent = this.defaultErrorHeader;
        }

        const fieldInputs = {
          email: this.emailInput,
          name: this.nameInput,
          message: this.messageInput,
          send_on: this.sendonInput
        };

        Object.entries(body).forEach(([key, value]) => {
          const message = Array.isArray(value)
            ? value.join(', ')
            : String(value);

          const input = fieldInputs[key];
          const errorMessageId =
            `RecipientForm-${key}-error-${this.dataset.sectionId}`;

          const errorMessageElement = this.querySelector(
            `[id="${errorMessageId}"]`
          );

          const errorTextElement =
            errorMessageElement?.querySelector('.error-message');

          if (this.errorMessageList) {
            const li = document.createElement('li');
            li.className = 'error-message';

            if (input) {
              const link = document.createElement('a');
              link.href = `#${input.id}`;
              link.textContent = message;
              li.appendChild(link);
            } else {
              li.textContent = message;
            }

            this.errorMessageList.appendChild(li);
          }

          if (errorTextElement) {
            errorTextElement.textContent = message;
            errorMessageElement.classList.remove('hidden');
          }

          if (input) {
            input.setAttribute('aria-invalid', 'true');

            if (errorTextElement) {
              input.setAttribute(
                'aria-describedby',
                errorMessageId
              );
            }
          }
        });
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.hidden = true;
        }

        if (this.errorMessageList) {
          this.errorMessageList.replaceChildren();
        }

        this.querySelectorAll(
          '.recipient-fields .form__message'
        ).forEach((field) => {
          field.classList.add('hidden');

          const textField = field.querySelector('.error-message');

          if (textField) {
            textField.textContent = '';
          }
        });

        this.inputFields().forEach((input) => {
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        this.checkboxInput.checked = false;
        this.onChange();
      }
    }
  );
}
